import client from './client';

export interface District {
  id: number;
  name_ru: string;
  name_kk: string | null;
  is_active: boolean;
}

export interface Street {
  id: number;
  name_ru: string;
  name_kk: string | null;
  district_id: number | null;
  source: string | null;
}

// GeoJSON types
export type GeoJSONPolygon = {
  type: 'Polygon';
  coordinates: number[][][]; // [[[lon, lat], ...], ...]
};
export type GeoJSONLineString = {
  type: 'LineString';
  coordinates: number[][];
};
export type GeoJSONMultiLineString = {
  type: 'MultiLineString';
  coordinates: number[][][];
};
export type GeoJSONPoint = { type: 'Point'; coordinates: [number, number] };
export type AnyGeom =
  | GeoJSONPolygon
  | GeoJSONLineString
  | GeoJSONMultiLineString
  | GeoJSONPoint
  | null;

export interface DistrictFeature {
  type: 'Feature';
  id: number;
  geometry: GeoJSONPolygon | null;
  properties: {
    id: number;
    name_ru: string;
    name_kk: string | null;
    is_active: boolean;
    source?: string;
    streets_count: number;
    buildings_count?: number;
  };
}

/** Улица района с фактическими диапазонами домов, попавших в его границы. */
export interface DistrictStreetAddresses {
  street: string;
  street_id: number | null;
  houses: number;
  ranges: string[];
  is_primary_district: boolean;
}

export interface DistrictAddresses {
  district_id: number;
  district_name: string;
  streets: DistrictStreetAddresses[];
  streets_count: number;
  houses_count: number;
  houses_without_district_total: number;
}

export interface BuildingFeature {
  type: 'Feature';
  id: number;
  geometry: AnyGeom;
  properties: {
    street: string | null;
    house_number: string | null;
    purpose_name: string | null;
    floors: number | null;
  };
}

export interface StreetFeature {
  type: 'Feature';
  id: number;
  geometry: AnyGeom;
  properties: {
    id: number;
    name_ru: string;
    name_kk: string | null;
    district_id: number | null;
    centroid: [number, number] | null;
  };
}

export interface FeatureCollection<F> {
  type: 'FeatureCollection';
  features: F[];
}

export const geoApi = {
  // Districts
  listDistricts: () => client.get<District[]>('/api/admin/geo/districts'),
  createDistrict: (data: { name_ru: string; name_kk?: string; geom?: GeoJSONPolygon }) =>
    client.post<District>('/api/admin/geo/districts', data),
  updateDistrict: (
    id: number,
    data: Partial<District> & { geom?: GeoJSONPolygon | null }
  ) => client.patch<District>(`/api/admin/geo/districts/${id}`, data),
  deleteDistrict: (id: number) =>
    client.delete(`/api/admin/geo/districts/${id}`),

  // GeoJSON exports
  districtsGeoJSON: () =>
    client.get<FeatureCollection<DistrictFeature>>('/api/admin/geo/districts/geojson'),
  streetsGeoJSON: (includeLines = true) =>
    client.get<FeatureCollection<StreetFeature>>('/api/admin/geo/streets/geojson', {
      params: { include_lines: includeLines },
    }),

  // Auto-assignment
  assignStreets: (districtId: number, mode: 'replace' | 'add' = 'replace') =>
    client.post<{
      district_id: number;
      assigned_count: number;
      streets: { id: number; name_ru: string }[];
    }>(`/api/admin/geo/districts/${districtId}/assign-streets`, null, {
      params: { mode },
    }),

  // Район → улицы → дома
  districtAddresses: (id: number) =>
    client.get<DistrictAddresses>(`/api/admin/geo/districts/${id}/addresses`),
  districtBuildings: (id: number) =>
    client.get<FeatureCollection<BuildingFeature>>(
      `/api/admin/geo/districts/${id}/buildings/geojson`),
  recomputeLinks: () =>
    client.post<{ updated: Record<string, number>; totals: Record<string, number> }>(
      '/api/admin/geo/districts/recompute-links'),

  // Streets
  listStreets: (params?: { district_id?: number; search?: string; limit?: number }) =>
    client.get<{ items: Street[]; total: number }>('/api/admin/geo/streets', { params }),
  createStreet: (data: { name_ru: string; name_kk?: string; district_id?: number }) =>
    client.post<Street>('/api/admin/geo/streets', data),
  updateStreet: (id: number, data: Partial<Street>) =>
    client.patch<Street>(`/api/admin/geo/streets/${id}`, data),
  deleteStreet: (id: number) =>
    client.delete(`/api/admin/geo/streets/${id}`),
};
